import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import supabase from '../data/supabase';
import T from '../styles/theme';

const CATEGORY_ORDER = ["talk_ratio", "objection_handling", "next_steps", "discovery_quality"];
const CATEGORY_LABELS = { talk_ratio: "TR", objection_handling: "OH", next_steps: "NS", discovery_quality: "DQ" };

function scoreColor(s) {
  if (s >= 7) return "#2E8B57";
  if (s >= 5) return "#D4763A";
  return "#E53935";
}

export default function CoachingPopup({ analysisId, contactName, date, duration, onClose }) {
  const [coaching, setCoaching] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!analysisId) return;
    let mounted = true;

    async function load() {
      const { data, error: err } = await supabase.from("call_coaching")
        .select("*")
        .eq("call_analysis_id", analysisId)
        .limit(1)
        .maybeSingle();

      if (!mounted) return;
      if (err) { setError("Failed to load coaching data"); setLoading(false); return; }
      setCoaching(data);
      setLoading(false);
    }
    load();

    // Subscribe for realtime updates (coaching generated after page load)
    const channel = supabase
      .channel("coaching-" + analysisId)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "call_coaching", filter: `call_analysis_id=eq.${analysisId}` }, (payload) => {
        if (mounted) { setCoaching(payload.new); setLoading(false); }
      })
      .subscribe();

    return () => { mounted = false; supabase.removeChannel(channel); };
  }, [analysisId]);

  // Sort insights by category priority
  const sortedInsights = coaching?.insights
    ? [...coaching.insights].sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category))
    : [];

  const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" };
  const modal = { width: 480, maxHeight: "80vh", background: "#2C2E33", borderRadius: 12, border: "1px solid rgba(217,219,221,0.12)", overflow: "auto", fontFamily: "inherit" };
  const card = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(217,219,221,0.08)", borderRadius: 8, padding: 14, marginBottom: 8 };

  const content = (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid rgba(217,219,221,0.08)", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 400, color: "#FAFAFA" }}>Call Coaching</div>
            <div style={{ fontSize: 11, color: "rgba(217,219,221,0.4)", marginTop: 3 }}>
              {contactName || "Unknown"}{date ? ` · ${new Date(date).toLocaleDateString()}` : ""}{duration ? ` · ${Math.floor(duration / 60)}m` : ""}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(217,219,221,0.4)", fontSize: 18, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
        </div>

        <div style={{ padding: "16px 24px 24px" }}>
          {loading && <div style={{ padding: 40, textAlign: "center", fontSize: 12, color: "rgba(217,219,221,0.4)" }}>Loading coaching insights...</div>}

          {error && <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: "#E53935" }}>{error}</div>}

          {!loading && !coaching && !error && (
            <div style={{ padding: 30, textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "rgba(217,219,221,0.5)" }}>Coaching not available yet</div>
              <div style={{ fontSize: 10, color: "rgba(217,219,221,0.3)", marginTop: 4 }}>Coaching insights are generated after call analysis completes.</div>
            </div>
          )}

          {coaching && (<>
            {/* Overall Score */}
            <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
              <div style={{
                width: 56, height: 56, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                border: `3px solid ${scoreColor(coaching.overall_score)}`, flexShrink: 0,
              }}>
                <span style={{ fontSize: 22, fontWeight: 300, color: scoreColor(coaching.overall_score) }}>{coaching.overall_score}</span>
              </div>
              <div>
                <div style={{ fontSize: 13, color: "#FAFAFA" }}>Overall Score</div>
                <div style={{ fontSize: 11, color: "rgba(217,219,221,0.4)" }}>
                  {coaching.overall_score >= 8 ? "Excellent call execution" : coaching.overall_score >= 6 ? "Good call with room to improve" : coaching.overall_score >= 4 ? "Several areas need attention" : "Significant coaching needed"}
                </div>
              </div>
            </div>

            {/* Insight Cards */}
            {sortedInsights.map((insight, i) => (
              <div key={i} style={card}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 9, fontWeight: 500, color: "rgba(217,219,221,0.4)", width: 20, textAlign: "center" }}>{CATEGORY_LABELS[insight.category] || "--"}</span>
                    <span style={{ fontSize: 12, fontWeight: 400, color: "#FAFAFA" }}>{insight.label}</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <div style={{ width: 60, height: 4, borderRadius: 2, background: "rgba(217,219,221,0.08)", overflow: "hidden" }}>
                      <div style={{ width: `${insight.score * 10}%`, height: "100%", background: scoreColor(insight.score), borderRadius: 2 }} />
                    </div>
                    <span style={{ fontSize: 10, color: scoreColor(insight.score), fontVariantNumeric: "tabular-nums", minWidth: 24, textAlign: "right" }}>{insight.score}/10</span>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: "rgba(217,219,221,0.6)", lineHeight: 1.6, marginBottom: 6 }}>{insight.finding}</div>
                <div style={{ fontSize: 11, color: "#6B83D4", lineHeight: 1.6 }}>{insight.suggestion}</div>
              </div>
            ))}
          </>)}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, document.body);
}
